/* eslint-disable no-unused-vars */
import React, { ReactNode, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart as RechartsRadarChart,
  Sector,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export const chartColors = {
  primary: '#1391C9',
  primarySoft: 'rgba(19, 145, 201, 0.12)',
  compare: 'rgba(19, 145, 201, 0.55)',
  grid: '#F2F2F2',
  axis: '#9E9E9E',
  strong: '#292929',
  body: '#5D5D5D',
};

const axisTick = { fill: chartColors.axis, fontSize: 12, fontFamily: 'Geist' };

export interface TrendPoint {
  label: string;
  value: number;
  compare?: number;
}

const formatNumber = (value: number) => value.toLocaleString('pt-BR');

const compactNumber = (value: number) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace('.0', '')}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace('.0', '')}k`;
  return String(value);
};

const ChartTooltip = ({ active, payload, label, compareLabel }: any) => {
  if (!active || !payload?.length) return null;
  const current = payload.find((p: any) => p.dataKey === 'value');
  const compare = payload.find((p: any) => p.dataKey === 'compare');
  return (
    <Box
      bg="surface.base"
      border="1px solid"
      borderColor="line.hairline"
      borderRadius="lg"
      boxShadow="popover"
      px="12px"
      py="8px"
      minW="140px"
    >
      <Text fontSize="12px" lineHeight="16px" color="content.subtle" mb="4px">
        {label}
      </Text>
      {current && (
        <Flex align="center" justify="space-between" gap="12px">
          <Flex align="center" gap="6px">
            <Box w="8px" h="8px" borderRadius="full" bg={chartColors.primary} />
            <Text fontSize="12px" color="content.body">
              Atual
            </Text>
          </Flex>
          <Text fontFamily="mono" fontSize="12px" color="content.strong">
            {formatNumber(current.value)}
          </Text>
        </Flex>
      )}
      {compare && compare.value != null && (
        <Flex align="center" justify="space-between" gap="12px" mt="2px">
          <Flex align="center" gap="6px">
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              border="1.5px dotted"
              borderColor={chartColors.primary}
            />
            <Text fontSize="12px" color="content.body">
              {compareLabel ?? 'Anterior'}
            </Text>
          </Flex>
          <Text fontFamily="mono" fontSize="12px" color="content.subtle">
            {formatNumber(compare.value)}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

interface TrendChartProps {
  data: TrendPoint[];
  height?: number;
  showCompare?: boolean;
  compareLabel?: string;
  area?: boolean;
  tickEvery?: number;
}

export const TrendChart = ({
  data,
  height = 260,
  showCompare = true,
  compareLabel,
  area = false,
  tickEvery,
}: TrendChartProps) => {
  let Chart: any = area ? AreaChart : LineChart;
  if (showCompare) Chart = ComposedChart;
  let interval = 0;
  if (tickEvery != null) interval = tickEvery;
  else if (data.length > 14) interval = Math.max(1, Math.floor(data.length / 6)) - 1;
  const [ticks, setTicks] = React.useState(6);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => setTicks(Math.max(2, Math.floor(el.clientWidth / 96)));
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (tickEvery == null && data.length > ticks) {
    interval = Math.max(0, Math.ceil(data.length / ticks) - 1);
  }
  return (
    <Box ref={containerRef} w="100%" h={`${height}px`}>
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={chartColors.primary} stopOpacity={0.16} />
              <stop offset="100%" stopColor={chartColors.primary} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={chartColors.grid} strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tick={axisTick}
            tickLine={false}
            axisLine={false}
            interval={interval}
            dy={8}
          />
          <YAxis
            tick={axisTick}
            tickLine={false}
            axisLine={false}
            tickFormatter={compactNumber}
            width={48}
            allowDecimals={false}
          />
          <Tooltip
            content={<ChartTooltip compareLabel={compareLabel} />}
            cursor={{ stroke: chartColors.grid, strokeWidth: 1 }}
          />
          {area ? (
            <Area
              type="monotone"
              dataKey="value"
              stroke={chartColors.primary}
              strokeWidth={1.5}
              fill="url(#trendFill)"
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0, fill: chartColors.primary }}
              isAnimationActive={false}
            />
          ) : (
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartColors.primary}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0, fill: chartColors.primary }}
              isAnimationActive={false}
            />
          )}
          {showCompare && (
            <Line
              type="monotone"
              dataKey="compare"
              stroke={chartColors.compare}
              strokeWidth={1.5}
              strokeDasharray="2 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
        </Chart>
      </ResponsiveContainer>
    </Box>
  );
};

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  tone?: 'positive' | 'negative' | 'neutral';
}

export const Sparkline = ({ data, width = 72, height = 24, tone = 'neutral' }: SparklineProps) => {
  const color = {
    positive: '#15B042',
    negative: '#D63D3D',
    neutral: chartColors.primary,
  }[tone];
  const points = data.map((value, index) => ({ i: index, value }));
  return (
    <Box w={`${width}px`} h={`${height}px`} flexShrink={0}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 2, right: 1, bottom: 2, left: 1 }}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export interface BarListItem {
  id: string | number;
  label: ReactNode;
  value: number;
  hint?: string;
  icon?: ReactNode;
}

interface BarListProps {
  items: BarListItem[];
  max?: number;
  formatValue?: (value: number) => string;
}

export const BarList = ({ items, max, formatValue = formatNumber }: BarListProps) => {
  const top = max ?? Math.max(1, ...items.map((item) => item.value));
  return (
    <Flex direction="column" gap="20px">
      {items.map((item) => (
        <Box key={item.id}>
          <Flex align="center" justify="space-between" gap="12px" mb="8px">
            <Flex align="center" gap="10px" minW={0}>
              {item.icon && (
                <Flex
                  w="28px"
                  h="28px"
                  align="center"
                  justify="center"
                  flexShrink={0}
                  borderRadius="md"
                  bg="surface.selected"
                  color="content.body"
                >
                  {item.icon}
                </Flex>
              )}
              <Text fontSize="13px" lineHeight="20px" color="content.strong" isTruncated minW={0}>
                {item.label}
              </Text>
            </Flex>
            <Flex align="baseline" gap="8px" flexShrink={0}>
              <Text fontFamily="mono" fontSize="12px" color="content.strong">
                {formatValue(item.value)}
              </Text>
              {item.hint && (
                <Text fontFamily="mono" fontSize="12px" color="content.subtle">
                  {item.hint}
                </Text>
              )}
            </Flex>
          </Flex>
          <Box h="6px" bg="surface.selected" borderRadius="2px" overflow="hidden">
            <Box
              h="100%"
              w={`${Math.round((item.value / top) * 100)}%`}
              bg={chartColors.primary}
              borderRadius="2px"
            />
          </Box>
        </Box>
      ))}
    </Flex>
  );
};

export const Delta = ({ value }: { value: number | null | undefined }) => {
  if (value == null || Number.isNaN(value)) {
    return (
      <Text fontFamily="mono" fontSize="12px" color="content.faint">
        —
      </Text>
    );
  }
  let color = 'content.faint';
  if (value > 0) color = 'state.success';
  if (value < 0) color = 'state.destructive';
  const sign = value > 0 ? '+' : '';
  return (
    <Text fontFamily="mono" fontSize="12px" color={color}>
      {`${sign}${value.toFixed(0)}%`}
    </Text>
  );
};

export const formatCompact = compactNumber;
export const formatFull = formatNumber;

export interface HeatmapDay {
  date: string;
  value: number;
}

interface ActivityHeatmapProps {
  days: HeatmapDay[];
  weeks?: number;
}

const heatLevels = ['#F2F2F2', '#C6E9F7', '#93D6F0', '#5FC2E9', '#1391C9'];

export const ActivityHeatmap = ({ days, weeks = 16 }: ActivityHeatmapProps) => {
  const [hover, setHover] = React.useState<{ x: number; y: number; day: HeatmapDay } | null>(null);
  const max = Math.max(1, ...days.map((d) => d.value));
  const level = (value: number) => {
    if (value === 0) return 0;
    const ratio = value / max;
    if (ratio < 0.25) return 1;
    if (ratio < 0.5) return 2;
    if (ratio < 0.75) return 3;
    return 4;
  };
  const total = weeks * 7;
  const padded = [...Array(Math.max(0, total - days.length)).fill(null), ...days.slice(-total)];
  const columns: (HeatmapDay | null)[][] = [];
  for (let i = 0; i < weeks; i += 1) columns.push(padded.slice(i * 7, i * 7 + 7));
  const weekdayLabels = ['', 'Seg', '', 'Qua', '', 'Sex', ''];
  const monthLabels = columns.map((column, index) => {
    const first = column.find(Boolean) as HeatmapDay | undefined;
    if (!first) return '';
    const date = new Date(first.date);
    const prev = columns[index - 1]?.find(Boolean) as HeatmapDay | undefined;
    const prevMonth = prev ? new Date(prev.date).getMonth() : -1;
    return date.getMonth() !== prevMonth
      ? date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')
      : '';
  });
  const cell = 14;
  const gap = 3;
  return (
    <Box position="relative" w="100%" overflowX="auto">
      <Flex gap={`${gap}px`} align="flex-start">
        <Flex direction="column" gap={`${gap}px`} pt="18px" pr="4px">
          {weekdayLabels.map((label, i) => (
            <Text
              key={`wd-${['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][i]}`}
              h={`${cell}px`}
              lineHeight={`${cell}px`}
              fontSize="10px"
              color="content.faint"
              w="24px"
            >
              {label}
            </Text>
          ))}
        </Flex>
        {columns.map((column, ci) => (
          <Flex
            key={column.find(Boolean)?.date ?? `empty-${weeks - ci}`}
            direction="column"
            gap={`${gap}px`}
          >
            <Text
              h="15px"
              lineHeight="15px"
              fontSize="10px"
              color="content.faint"
              whiteSpace="nowrap"
            >
              {monthLabels[ci]}
            </Text>
            {column.map((day, di) => (
              <Box
                key={day?.date ?? `${ci}-${['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][di]}`}
                w={`${cell}px`}
                h={`${cell}px`}
                borderRadius="3px"
                bg={day ? heatLevels[level(day.value)] : 'transparent'}
                cursor={day ? 'default' : undefined}
                onMouseEnter={(e) => day && setHover({ x: e.clientX, y: e.clientY, day })}
                onMouseLeave={() => setHover(null)}
              />
            ))}
          </Flex>
        ))}
      </Flex>
      <Flex align="center" gap="6px" mt="12px" justify="flex-end">
        <Text fontSize="10px" color="content.faint">
          Menos
        </Text>
        {heatLevels.map((color) => (
          <Box key={color} w="10px" h="10px" borderRadius="2px" bg={color} />
        ))}
        <Text fontSize="10px" color="content.faint">
          Mais
        </Text>
      </Flex>
      {hover && (
        <Box
          position="fixed"
          left={`${hover.x + 12}px`}
          top={`${hover.y + 12}px`}
          zIndex={1500}
          bg="surface.base"
          border="1px solid"
          borderColor="line.hairline"
          borderRadius="lg"
          boxShadow="popover"
          px="10px"
          py="6px"
          pointerEvents="none"
        >
          <Text fontSize="12px" color="content.subtle">
            {new Date(hover.day.date).toLocaleDateString('pt-BR', {
              weekday: 'short',
              day: '2-digit',
              month: 'short',
            })}
          </Text>
          <Text fontFamily="mono" fontSize="12px" color="content.strong">
            {formatNumber(hover.day.value)} {hover.day.value === 1 ? 'acesso' : 'acessos'}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export interface DonutSlice {
  id: string;
  label: string;
  value: number;
}

const donutPalette = ['#0E6FA0', '#1391C9', '#4FB3E0', '#8ACFEE', '#BFE4F7'];

export const DonutChart = ({ slices, size = 150 }: { slices: DonutSlice[]; size?: number }) => {
  const [active, setActive] = useState<number | null>(null);
  const total = Math.max(
    1,
    slices.reduce((acc, s) => acc + s.value, 0)
  );
  const outer = size / 2;
  const inner = outer - 24;
  const current = active != null ? slices[active] : null;
  const colorAt = (index: number) => donutPalette[index % donutPalette.length];

  return (
    <Flex direction="column" align="center" gap="20px" w="100%">
      <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
        <PieChart width={size} height={size}>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={inner}
            outerRadius={outer - 4}
            startAngle={90}
            endAngle={-270}
            stroke="none"
            isAnimationActive={false}
            activeIndex={active ?? undefined}
            activeShape={(props: any) => <Sector {...props} outerRadius={props.outerRadius + 4} />}
            onMouseEnter={(_: any, index: number) => setActive(index)}
            onMouseLeave={() => setActive(null)}
          >
            {slices.map((slice, index) => (
              <Cell
                key={slice.id}
                fill={colorAt(index)}
                opacity={active == null || active === index ? 1 : 0.45}
                style={{ cursor: 'default', transition: 'opacity 120ms ease' }}
              />
            ))}
          </Pie>
        </PieChart>
        <Flex
          position="absolute"
          inset={0}
          direction="column"
          align="center"
          justify="center"
          pointerEvents="none"
          px="18px"
          textAlign="center"
        >
          <Text
            fontFamily="display"
            fontSize="24px"
            lineHeight="30px"
            fontWeight={500}
            color="content.strong"
          >
            {compactNumber(current ? current.value : total)}
          </Text>
          <Text fontSize="11px" color="content.faint" noOfLines={1}>
            {current ? current.label : 'acessos'}
          </Text>
        </Flex>
      </Box>
      <Flex direction="column" gap="2px" w="100%" maxW="260px">
        {slices.map((slice, index) => (
          <Flex
            key={slice.id}
            align="center"
            justify="space-between"
            gap="12px"
            h="26px"
            px="6px"
            mx="-6px"
            borderRadius="4px"
            bg={active === index ? 'surface.selected' : 'transparent'}
            transition="background 120ms ease"
            onMouseEnter={() => setActive(index)}
            onMouseLeave={() => setActive(null)}
          >
            <Flex align="center" gap="8px" minW={0}>
              <Box w="8px" h="8px" borderRadius="2px" bg={colorAt(index)} flexShrink={0} />
              <Text fontSize="13px" color="content.body" whiteSpace="nowrap">
                {slice.label}
              </Text>
            </Flex>
            <Flex align="baseline" gap="8px" flexShrink={0}>
              <Text fontFamily="mono" fontSize="12px" color="content.strong">
                {formatNumber(slice.value)}
              </Text>
              <Text
                fontFamily="mono"
                fontSize="12px"
                color="content.subtle"
                minW="32px"
                textAlign="right"
              >
                {Math.round((slice.value / total) * 100)}%
              </Text>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export interface RadarPoint {
  name: string;
  value: number | null;
}

const RadarTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const point = payload[0];
  if (point?.value == null) return null;
  return (
    <Box
      bg="surface.base"
      border="1px solid"
      borderColor="line.hairline"
      borderRadius="lg"
      boxShadow="popover"
      px="12px"
      py="8px"
    >
      <Text fontSize="12px" lineHeight="16px" color="content.subtle" mb="2px">
        {point.payload?.name}
      </Text>
      <Text fontFamily="mono" fontSize="12px" color="content.strong">
        {Number(point.value).toFixed(1)}
      </Text>
    </Box>
  );
};

export const RadarChart = ({
  points,
  height = 260,
  max = 10,
}: {
  points: RadarPoint[];
  height?: number;
  max?: number;
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <RechartsRadarChart data={points} cx="50%" cy="50%" outerRadius="72%">
      <PolarGrid stroke={chartColors.grid} />
      <PolarAngleAxis
        dataKey="name"
        tick={{ fill: '#8A8A8A', fontSize: 11, fontFamily: 'Geist' }}
      />
      <PolarRadiusAxis domain={[0, max]} axisLine={false} tick={false} />
      <Tooltip content={<RadarTooltip />} cursor={{ stroke: chartColors.grid, strokeWidth: 1 }} />
      <Radar
        dataKey="value"
        stroke={chartColors.primary}
        strokeWidth={1.5}
        fill={chartColors.primary}
        fillOpacity={0.16}
        dot={{ r: 2.5, strokeWidth: 0, fill: chartColors.primary }}
        isAnimationActive={false}
      />
    </RechartsRadarChart>
  </ResponsiveContainer>
);
